import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BuilderComponent, builder } from "@builder.io/react";
import { motion } from "framer-motion";

import Footer from "../components/Footer";

const builderKey = process.env.REACT_APP_BUILDER_TOKEN;
builder.init(builderKey);

export default function Page() {
  const [homepage, setHomepage] = useState(null);
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    builder
      .get("page", {
        query: {
          data: {
            uuid: 1,
          },
        },
      })
      .toPromise()
      .then((homepageData) => {
        setHomepage(homepageData);
        setLoading(false);
      });
  }, []);

  return (
    <>
      {homepage && <BuilderComponent model="homepage" content={homepage} />}
      {!isLoading && (
        <>
          <motion.section 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="about-us-area relative py-16 sm:py-20 lg:py-24 overflow-hidden"
          >
            {/* Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900/20 to-purple-900/20"></div>
            
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <div className="max-w-4xl mx-auto">
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-premium-xl border border-white/10 p-8 sm:p-12 lg:p-16"
                >
                  <div className="text-center space-y-6 sm:space-y-8">
                    {/* Decorative Elements */}
                    <div className="flex justify-center items-center space-x-4 mb-8">
                      <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>
                      <div className="w-16 h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent"></div>
                      <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full"></div>
                    </div>

                    {/* Main Content */}
                    <motion.h2 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.6 }}
                      className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight"
                    >
                      <span className="text-white">
                        <Link 
                          to="/about" 
                          className="inline- ck bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text text-transparent hover:from-blue-300 hover:via-purple-300 hover:to-blue-300 transition-all duration-300 transform hover:scale-105"
                        >
                          Andy Cary
                        </Link>
                      </span>
                      <span className="text-gray-300 block sm:inline sm:ml-2">
                        is a Senior Software Engineer specializing in building usable, performant, reliable and maintainable web applications.
                      </span>
                    </motion.h2>

                    {/* Call to Action */}
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.8 }}
                      className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 pt-8"
                    >
                      <Link
                        to="/portfolio"
                        className="group inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-semibold rounded-xl shadow-premium-lg hover:shadow-premium-xl transform hover:scale-105 transition-all duration-300"
                      >
                        <span>View Portfolio</span>
                        <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                      
                      <Link
                        to="/contact"
                        className="group inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold rounded-xl border border-white/20 hover:border-white/30 shadow-premium hover:shadow-premium-lg transform hover:scale-105 transition-all duration-300"
                      >
                        <span>Get in Touch</span>
                        <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </Link>
                    </motion.div>

                    {/* Decorative Elements */}
                    <div className="flex justify-center items-center space-x-4 mt-8">
                      <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full"></div>
                      <div className="w-16 h-px bg-gradient-to-r from-transparent via-purple-400 to-transparent"></div>
                      <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.section>
          <Footer />
        </>
      )}
    </>
  );
}
